import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Collapse, DatePicker, Divider, Dropdown, Form, Input, InputNumber,
  Modal, Select, Space, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CopyOutlined, EditOutlined, EyeOutlined, KeyOutlined, MoreOutlined, PlusOutlined, ReloadOutlined, SettingOutlined, StopOutlined, SyncOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import API_BASE_URL, { ENDPOINTS } from '../../helpers/api/apiConfig';
import { COMMAND_CATALOGUE } from '../Operations/missionCommandCatalogue';
import ModuleFormList from './ModuleFormList';
import { deepMergeConfig } from './ModuleConfigEditor';
import DeviceTypeDetail from './DeviceTypeDetail';
import type {
  ModuleDefinitionItem, CompanyOption,
  DeviceAuthTokenItem, DeviceCapabilityDefinitionItem, DeviceConfigurationItem, DeviceItem,
  DeviceVideoStreamItem,
  DeviceTypeCapabilityItem, DeviceTypeItem, EffectiveDeviceCapabilityItem, VehicleCapabilities,
  VehicleProfileItem,
} from './types';

const { Text } = Typography;

/* ── Helpers ── */

type CompanyFilterValue = 'all' | 'unassigned' | number;

const statusColorMap: Record<string, string> = {
  online: 'green', connected: 'green', ready: 'blue', offline: 'default',
  unknown: 'default', telemetry: 'cyan', maintenance: 'gold', error: 'red',
};
const capabilityCategoryLabels: Record<string, string> = {
  navigation: 'Navigation',
  mapping: 'Mapping',
  autonomy: 'Autonomy',
  localization: 'Localization',
  safety: 'Safety',
  payload: 'Payload',
  connectivity: 'Connectivity',
  other: 'Other',
};
const capabilityCategoryColors: Record<string, string> = {
  navigation: 'blue',
  mapping: 'purple',
  autonomy: 'cyan',
  localization: 'geekblue',
  safety: 'gold',
  payload: 'volcano',
  connectivity: 'green',
  other: 'default',
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return <Text type="secondary">Never</Text>;
  const ts = dayjs(value);
  return ts.isValid()
    ? <Text title={ts.toISOString()}>{ts.format('DD MMM YYYY HH:mm')}</Text>
    : <Text type="secondary">Unknown</Text>;
};

const normalizeOptionalField = (v: unknown): string | null => {
  const n = typeof v === 'string' ? v.trim() : '';
  return n || null;
};

const normalizeCompanyNameKey = (v?: string | null) => String(v || '').trim().toLowerCase();

const getDeviceStatusLabel = (value: unknown): string => {
  if (!value) return 'unknown';

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return 'unknown';
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try { return getDeviceStatusLabel(JSON.parse(trimmed)); } catch { return 'telemetry'; }
    }
    return trimmed.length <= 32 ? trimmed.toLowerCase() : 'telemetry';
  }

  if (typeof value === 'object') {
    const snapshot = value as Record<string, unknown>;
    const candidate = snapshot.currentStatus ?? snapshot.status ?? snapshot.state;
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim().toLowerCase();
    if (snapshot.connected === true) return 'connected';
    return 'telemetry';
  }

  return 'unknown';
};

const stringifyJson = (value: unknown) => {
  if (value == null) return '';
  try { return JSON.stringify(value, null, 2); } catch { return ''; }
};

const findModuleDefinition = (definitions: ModuleDefinitionItem[], id: unknown): ModuleDefinitionItem | undefined => {
  if (id === undefined || id === null || id === '') return undefined;
  const numericId = Number(id);
  return definitions.find((definition) => Number(definition.id) === numericId);
};

const mergeModuleConfigDefaults = (definitions: ModuleDefinitionItem[], moduleDefinitionId: unknown, config: unknown, embeddedDefinition?: any): Record<string, any> => {
  const definition = findModuleDefinition(definitions, moduleDefinitionId) || embeddedDefinition;
  const currentConfig = config && typeof config === 'object' && !Array.isArray(config) ? config : {};
  return definition?.defaultConfig ? deepMergeConfig(definition.defaultConfig, currentConfig) : currentConfig as Record<string, any>;
};

const parseJsonObjectText = (value: unknown, label: string, optional = false): Record<string, any> | null => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) { if (optional) return null; throw new Error(`${label} is required`); }
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') throw new Error('invalid');
    return parsed;
  } catch { throw new Error(`${label} must be a valid JSON object`); }
};

/* ── Main component ── */

interface BackOfficeDevicesTabProps {
  buildAuthHeaders: (includeContentType?: boolean) => Record<string, string>;
  onAuthMismatch: (response: Response, errorData: any) => Promise<boolean>;
}

const BackOfficeDevicesTab: React.FC<BackOfficeDevicesTabProps> = ({ buildAuthHeaders, onAuthMismatch }) => {
  /* ── State ── */
  const [devices, setDevices] = useState<DeviceItem[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceTypeItem[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [moduleDefinitions, setModuleDefinitions] = useState<ModuleDefinitionItem[]>([]);
  const [capabilityDefinitions, setCapabilityDefinitions] = useState<DeviceCapabilityDefinitionItem[]>([]);
  const [vehicleProfiles, setVehicleProfiles] = useState<VehicleProfileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [typeLoading, setTypeLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [typeSaving, setTypeSaving] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [streamModalOpen, setStreamModalOpen] = useState(false);
  const [editing, setEditing] = useState<DeviceItem | null>(null);
  const [configDevice, setConfigDevice] = useState<DeviceItem | null>(null);
  const [tokenDevice, setTokenDevice] = useState<DeviceItem | null>(null);
  const [streamDevice, setStreamDevice] = useState<DeviceItem | null>(null);
  const [configurations, setConfigurations] = useState<DeviceConfigurationItem[]>([]);
  const [deviceTokens, setDeviceTokens] = useState<DeviceAuthTokenItem[]>([]);
  const [deviceStreams, setDeviceStreams] = useState<DeviceVideoStreamItem[]>([]);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenSaving, setTokenSaving] = useState(false);
  const [streamLoading, setStreamLoading] = useState(false);
  const [streamSaving, setStreamSaving] = useState(false);
  const [syncingDeviceId, setSyncingDeviceId] = useState<number | null>(null);
  const [companyFilter, setCompanyFilter] = useState<CompanyFilterValue>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [typeSearchTerm, setTypeSearchTerm] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [typeForm] = Form.useForm();
  const [configForm] = Form.useForm();
  const [tokenForm] = Form.useForm();
  const [streamForm] = Form.useForm();

  /* ── Data fetching ── */
  const fetchCompanies = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/companies?scope=all`, { headers: buildAuthHeaders(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const map = new Map<number, CompanyOption>();
        (Array.isArray(data) ? data : []).forEach((item: any) => {
          const id = Number(item.id); const name = String(item.name || '').trim();
          if (Number.isFinite(id) && id > 0 && name && !map.has(id)) map.set(id, { id, name });
        });
        setCompanies(Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id));
      } else { const err = await res.json(); if (await onAuthMismatch(res, err)) return; message.error(err?.message || 'Failed to load companies'); }
    } catch (e) { console.error(e); message.error('Failed to load companies'); }
  }, [buildAuthHeaders, onAuthMismatch]);

  const fetchDeviceTypes = useCallback(async () => {
    setTypeLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/device-types`, { headers: buildAuthHeaders(), credentials: 'include' });
      if (res.ok) { const d = await res.json(); setDeviceTypes(Array.isArray(d?.data) ? d.data : []); }
      else { const err = await res.json(); if (await onAuthMismatch(res, err)) return; message.error(err?.message || 'Failed to load device types'); }
    } catch (e) { console.error(e); message.error('Failed to load device types'); }
    finally { setTypeLoading(false); }
  }, [buildAuthHeaders, onAuthMismatch]);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(); params.set('scope', 'all');
      if (companyFilter === 'unassigned') params.set('assignment_status', 'available');
      else if (typeof companyFilter === 'number') params.set('company_id', String(companyFilter));
      const res = await fetch(`${API_BASE_URL}/devices?${params}`, { headers: buildAuthHeaders(), credentials: 'include' });
      if (res.ok) { const d = await res.json(); setDevices(Array.isArray(d?.data) ? d.data : []); }
      else { const err = await res.json(); if (await onAuthMismatch(res, err)) return; message.error(err?.message || 'Failed to load devices'); }
    } catch (e) { console.error(e); message.error('Failed to load devices'); }
    finally { setLoading(false); }
  }, [buildAuthHeaders, companyFilter, onAuthMismatch]);

  const fetchModuleDefinitions = useCallback(async () => {
    try {
      const res = await fetch(ENDPOINTS.MODULE_DEFINITIONS, { headers: buildAuthHeaders(), credentials: 'include' });
      if (res.ok) { const d = await res.json(); setModuleDefinitions(Array.isArray(d?.data) ? d.data : []); }
    } catch (e) { console.error(e); }
  }, [buildAuthHeaders]);

  const fetchCapabilityDefinitions = useCallback(async () => {
    try {
      const res = await fetch(ENDPOINTS.DEVICE_CAPABILITY_DEFINITIONS, { headers: buildAuthHeaders(), credentials: 'include' });
      if (res.ok) { const d = await res.json(); setCapabilityDefinitions(Array.isArray(d?.data) ? d.data : []); }
    } catch (e) { console.error(e); }
  }, [buildAuthHeaders]);

  const fetchDeviceConfigurations = useCallback(async (deviceId: number) => {
    setConfigLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/devices/${deviceId}/configurations`, { headers: buildAuthHeaders(), credentials: 'include' });
      if (res.ok) { const d = await res.json(); setConfigurations(Array.isArray(d?.data) ? d.data : []); }
      else { const err = await res.json(); if (await onAuthMismatch(res, err)) return; message.error(err?.message || 'Failed to load configurations'); }
    } catch (e) { console.error(e); message.error('Failed to load configurations'); }
    finally { setConfigLoading(false); }
  }, [buildAuthHeaders, onAuthMismatch]);

  const fetchDeviceTokens = useCallback(async (deviceId: number) => {
    setTokenLoading(true);
    try {
      const res = await fetch(ENDPOINTS.DEVICE_AUTH_TOKENS(deviceId), { headers: buildAuthHeaders(), credentials: 'include' });
      if (res.ok) { const d = await res.json(); setDeviceTokens(Array.isArray(d?.data) ? d.data : []); }
      else { const err = await res.json(); if (await onAuthMismatch(res, err)) return; message.error(err?.message || 'Failed to load device tokens'); }
    } catch (e) { console.error(e); message.error('Failed to load device tokens'); }
    finally { setTokenLoading(false); }
  }, [buildAuthHeaders, onAuthMismatch]);

  const fetchDeviceStreams = useCallback(async (deviceId: number) => {
    setStreamLoading(true);
    try {
      const res = await fetch(ENDPOINTS.DEVICE_VIDEO_STREAMS(deviceId), { headers: buildAuthHeaders(), credentials: 'include' });
      if (res.ok) { const d = await res.json(); setDeviceStreams(Array.isArray(d?.data) ? d.data : []); }
      else { const err = await res.json(); if (await onAuthMismatch(res, err)) return; message.error(err?.message || 'Failed to load video streams'); }
    } catch (e) { console.error(e); message.error('Failed to load video streams'); }
    finally { setStreamLoading(false); }
  }, [buildAuthHeaders, onAuthMismatch]);

  const fetchVehicleProfiles = useCallback(async () => {
    try {
      const res = await fetch(ENDPOINTS.VEHICLE_PROFILES, { headers: buildAuthHeaders(), credentials: 'include' });
      if (res.ok) { const d = await res.json(); setVehicleProfiles(Array.isArray(d?.data) ? d.data : []); }
    } catch (e) { console.error(e); }
  }, [buildAuthHeaders]);

  useEffect(() => { fetchCompanies(); fetchDeviceTypes(); fetchModuleDefinitions(); fetchCapabilityDefinitions(); fetchVehicleProfiles(); }, [fetchCompanies, fetchDeviceTypes, fetchModuleDefinitions, fetchCapabilityDefinitions, fetchVehicleProfiles]);
  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  /* ── Formatting helpers ── */
  const duplicateCompanyNameCounts = useMemo(() => {
    const m = new Map<string, number>();
    companies.forEach((c) => { const k = normalizeCompanyNameKey(c.name); m.set(k, (m.get(k) || 0) + 1); });
    return m;
  }, [companies]);

  const formatCompanyLabel = useCallback((c?: CompanyOption | null) => {
    if (!c) return 'Unassigned';
    return (duplicateCompanyNameCounts.get(normalizeCompanyNameKey(c.name)) || 0) > 1 ? `${c.name} (#${c.id})` : c.name;
  }, [duplicateCompanyNameCounts]);

  const resolveDeviceType = useCallback((d: DeviceItem): DeviceTypeItem | null => {
    const id = d.deviceType?.id ?? d.deviceTypeId ?? null;
    if (!id) return null;
    const full = deviceTypes.find((t) => t.id === id);
    if (full) return full;
    if (d.deviceType) return { id: d.deviceType.id, name: d.deviceType.name, description: d.deviceType.description ?? null, moduleTemplates: d.deviceType.moduleTemplates ? [...d.deviceType.moduleTemplates] : [], capabilities: d.deviceType.capabilities ? [...d.deviceType.capabilities] : [] };
    return null;
  }, [deviceTypes]);

  const capabilitySelectOptions = useMemo(() => {
    const byCategory = new Map<string, DeviceCapabilityDefinitionItem[]>();
    capabilityDefinitions.forEach((definition) => {
      const category = definition.category || 'other';
      byCategory.set(category, [...(byCategory.get(category) || []), definition]);
    });
    return Array.from(byCategory.entries())
      .sort(([left], [right]) => (capabilityCategoryLabels[left] || left).localeCompare(capabilityCategoryLabels[right] || right))
      .map(([category, definitions]) => ({
        label: capabilityCategoryLabels[category] || category,
        options: definitions
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.name.localeCompare(b.name))
          .map((definition) => ({
            value: definition.id,
            label: definition.name,
          })),
      }));
  }, [capabilityDefinitions]);

  const buildCapabilityPayload = useCallback((ids: number[] = []) => (
    ids.map((definitionId, index) => ({
      capabilityDefinitionId: definitionId,
      enabled: true,
      source: 'device_type',
      sortOrder: index,
    }))
  ), []);

  const renderCapabilityTags = useCallback((capabilities?: Array<DeviceTypeCapabilityItem | EffectiveDeviceCapabilityItem> | null) => {
    const enabled = (capabilities || []).filter((capability) => capability.enabled !== false);
    if (enabled.length === 0) return <Text type="secondary">None</Text>;
    const visible = enabled.slice(0, 3);
    const hidden = enabled.slice(3);
    const capabilityLabel = (capability: DeviceTypeCapabilityItem | EffectiveDeviceCapabilityItem) => {
      const definition = capability.definition || capability.capabilityDefinition || null;
      return capability.name || definition?.name || capability.key || definition?.key || String(capability.capabilityDefinitionId || capability.id);
    };
    return (
      <Space wrap size={[4, 4]} className="back-office-device-tags">
        {visible.map((capability) => {
          const definition = capability.definition || capability.capabilityDefinition || null;
          const key = capability.key || definition?.key || String(capability.capabilityDefinitionId || capability.id || capability.name);
          const category = capability.category || definition?.category || 'other';
          return (
            <Tag key={key} color={capabilityCategoryColors[category] || 'default'} title={capabilityLabel(capability)}>
              {capabilityLabel(capability)}
            </Tag>
          );
        })}
        {hidden.length > 0 && (
          <Tooltip title={hidden.map(capabilityLabel).join(', ')}>
            <Tag>+{hidden.length}</Tag>
          </Tooltip>
        )}
      </Space>
    );
  }, []);

  /* ── Filtered lists ── */
  const filteredDeviceTypes = useMemo(() => {
    const q = typeSearchTerm.trim().toLowerCase();
    const sorted = [...deviceTypes].sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id);
    if (!q) return sorted;
    return sorted.filter((t) => [String(t.id), t.name, t.description, ...(t.moduleTemplates || []).map((c) => c.moduleDefinition?.key || c.name)].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [deviceTypes, typeSearchTerm]);

  const filteredDevices = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const sorted = [...devices].sort((a, b) => (b.createdAt ? dayjs(b.createdAt).valueOf() : 0) - (a.createdAt ? dayjs(a.createdAt).valueOf() : 0));
    if (!q) return sorted;
    return sorted.filter((d) => {
      const t = resolveDeviceType(d);
      return [String(d.id), d.label, d.serialNumber, d.codePrefix, d.company?.name, d.currentStatus, t?.name].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [devices, resolveDeviceType, searchTerm]);

  /* ── Modal handlers ── */
  const openCreateDevice = useCallback(() => { setEditing(null); form.resetFields(); setModalOpen(true); }, [form]);
  const openEditDevice = useCallback((d: DeviceItem) => {
    setEditing(d);
    form.setFieldsValue({
      label: d.label,
      codePrefix: d.codePrefix ?? undefined,
      serialNumber: d.serialNumber ?? undefined,
      companyId: d.company?.id ?? d.companyId ?? undefined,
      deviceTypeId: d.deviceType?.id ?? d.deviceTypeId ?? undefined,
      modules: (d.modules || []).map((module) => {
          const moduleDefinitionId = module.moduleDefinitionId ?? module.moduleDefinition?.id ?? undefined;
          return {
            moduleDefinitionId,
            moduleDefinition: module.moduleDefinition ?? undefined,
            name: module.name ?? undefined,
            instanceKey: module.instanceKey ?? undefined,
            status: module.status || 'installed',
            isDefault: module.isDefault ?? false,
            config: mergeModuleConfigDefaults(moduleDefinitions, moduleDefinitionId, module.config, module.moduleDefinition),
            configText: stringifyJson(module.config),
            metadataText: stringifyJson(module.metadata),
          };
      }),
    });
    setModalOpen(true);
  }, [form, moduleDefinitions]);
  const closeDeviceModal = useCallback(() => { setModalOpen(false); setEditing(null); form.resetFields(); }, [form]);

  const openCreateType = useCallback(() => { typeForm.resetFields(); typeForm.setFieldsValue({ moduleTemplates: [], capabilityIds: [] }); setTypeModalOpen(true); }, [typeForm]);
  const closeTypeModal = useCallback(() => { setTypeModalOpen(false); typeForm.resetFields(); }, [typeForm]);

  const openConfigModal = useCallback(async (d: DeviceItem) => {
    setConfigDevice(d); setConfigurations([]);
    configForm.resetFields(); configForm.setFieldsValue({ configJsonText: '{\n  \n}', appliedAt: null });
    setConfigModalOpen(true); await fetchDeviceConfigurations(d.id);
  }, [configForm, fetchDeviceConfigurations]);
  const closeConfigModal = useCallback(() => { setConfigModalOpen(false); setConfigDevice(null); setConfigurations([]); configForm.resetFields(); }, [configForm]);

  const showIssuedToken = useCallback((token?: DeviceAuthTokenItem | null) => {
    if (!token?.token) return;
    Modal.info({
      title: 'Device credential created',
      width: 680,
      content: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Alert type="warning" showIcon message="This credential is shown once. Store it securely on the physical device now." />
          <Input.TextArea value={token.token} autoSize={{ minRows: 2, maxRows: 4 }} readOnly />
          <Button icon={<CopyOutlined />} onClick={() => navigator.clipboard?.writeText(token.token || '')}>
            Copy credential
          </Button>
        </Space>
      ),
    });
  }, []);

  const openTokenModal = useCallback(async (d: DeviceItem) => {
    setTokenDevice(d); setDeviceTokens([]);
    tokenForm.resetFields(); tokenForm.setFieldsValue({ name: `${d.label} token` });
    setTokenModalOpen(true); await fetchDeviceTokens(d.id);
  }, [fetchDeviceTokens, tokenForm]);
  const closeTokenModal = useCallback(() => { setTokenModalOpen(false); setTokenDevice(null); setDeviceTokens([]); tokenForm.resetFields(); }, [tokenForm]);

  const openStreamModal = useCallback(async (d: DeviceItem) => {
    setStreamDevice(d); setDeviceStreams([]);
    streamForm.resetFields(); streamForm.setFieldsValue({ cameraKey: 'front', label: 'Front camera', enabled: true });
    setStreamModalOpen(true); await fetchDeviceStreams(d.id);
  }, [fetchDeviceStreams, streamForm]);
  const closeStreamModal = useCallback(() => { setStreamModalOpen(false); setStreamDevice(null); setDeviceStreams([]); streamForm.resetFields(); }, [streamForm]);

  /* ── Save handlers ── */
  const saveDevice = useCallback(async () => {
    try {
      const v = await form.validateFields();
      const modules = Array.isArray(v.modules)
        ? v.modules.map((item: any, i: number) => ({
            moduleDefinitionId: item?.moduleDefinitionId ?? null,
            name: normalizeOptionalField(item?.name),
            instanceKey: normalizeOptionalField(item?.instanceKey),
            status: item?.status || 'installed',
            isDefault: item?.isDefault ?? false,
            config: item?.config && typeof item.config === 'object' && !Array.isArray(item.config)
              ? mergeModuleConfigDefaults(moduleDefinitions, item?.moduleDefinitionId, item.config, item?.moduleDefinition)
              : parseJsonObjectText(item?.configText, `Device module ${i + 1} config`, true),
            metadata: parseJsonObjectText(item?.metadataText, `Device module ${i + 1} metadata`, true),
          }))
        : undefined;
      setSaving(true);
      const res = await fetch(editing ? `${API_BASE_URL}/devices/${editing.id}` : `${API_BASE_URL}/devices`, {
        method: editing ? 'PUT' : 'POST', headers: buildAuthHeaders(), credentials: 'include',
        body: JSON.stringify({
          label: String(v.label || '').trim(),
          codePrefix: normalizeOptionalField(v.codePrefix),
          serialNumber: normalizeOptionalField(v.serialNumber),
          companyId: v.companyId ?? null,
          deviceTypeId: v.deviceTypeId ?? null,
          ...(Array.isArray(v.modules) ? { modules } : {}),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        showIssuedToken(data?.deviceAuthToken || data?.data?.deviceAuthToken || null);
        message.success(editing ? 'Device updated' : 'Device created');
        closeDeviceModal();
        try { await Promise.all([fetchDevices(), fetchDeviceTypes()]); } catch (refreshErr) { console.warn('Device saved but list refresh failed:', refreshErr); }
      } else { const err = await res.json(); if (await onAuthMismatch(res, err)) return; message.error(err?.message || 'Failed to save device'); }
    } catch (e: any) { if (e?.errorFields) return; message.error('Failed to save device'); }
    finally { setSaving(false); }
  }, [buildAuthHeaders, closeDeviceModal, editing, fetchDevices, fetchDeviceTypes, form, moduleDefinitions, onAuthMismatch, showIssuedToken]);

  const createDeviceToken = useCallback(async () => {
    if (!tokenDevice) return;
    try {
      const v = await tokenForm.validateFields();
      setTokenSaving(true);
      const res = await fetch(ENDPOINTS.DEVICE_AUTH_TOKENS(tokenDevice.id), {
        method: 'POST', headers: buildAuthHeaders(), credentials: 'include',
        body: JSON.stringify({ name: String(v.name || '').trim() || 'Device token' }),
      });
      if (res.ok) {
        const d = await res.json();
        showIssuedToken(d?.data || null);
        tokenForm.setFieldsValue({ name: `${tokenDevice.label} token` });
        await fetchDeviceTokens(tokenDevice.id);
      } else { const err = await res.json(); if (await onAuthMismatch(res, err)) return; message.error(err?.message || 'Failed to create token'); }
    } catch (e: any) { if (e?.errorFields) return; message.error('Failed to create token'); }
    finally { setTokenSaving(false); }
  }, [buildAuthHeaders, fetchDeviceTokens, onAuthMismatch, showIssuedToken, tokenDevice, tokenForm]);

  const revokeDeviceToken = useCallback(async (token: DeviceAuthTokenItem) => {
    if (!tokenDevice) return;
    const res = await fetch(ENDPOINTS.DEVICE_AUTH_TOKEN_REVOKE(tokenDevice.id, token.id), {
      method: 'POST', headers: buildAuthHeaders(), credentials: 'include',
    });
    if (res.ok) {
      message.success('Token revoked');
      await fetchDeviceTokens(tokenDevice.id);
    } else { const err = await res.json(); if (await onAuthMismatch(res, err)) return; message.error(err?.message || 'Failed to revoke token'); }
  }, [buildAuthHeaders, fetchDeviceTokens, onAuthMismatch, tokenDevice]);

  const rotateDeviceToken = useCallback(async (token: DeviceAuthTokenItem) => {
    if (!tokenDevice) return;
    const res = await fetch(ENDPOINTS.DEVICE_AUTH_TOKEN_ROTATE(tokenDevice.id, token.id), {
      method: 'POST', headers: buildAuthHeaders(), credentials: 'include',
      body: JSON.stringify({ name: `${token.name} rotation` }),
    });
    if (res.ok) {
      const d = await res.json();
      showIssuedToken(d?.data || null);
      await fetchDeviceTokens(tokenDevice.id);
    } else { const err = await res.json(); if (await onAuthMismatch(res, err)) return; message.error(err?.message || 'Failed to rotate token'); }
  }, [buildAuthHeaders, fetchDeviceTokens, onAuthMismatch, showIssuedToken, tokenDevice]);

  const saveDeviceStream = useCallback(async () => {
    if (!streamDevice) return;
    try {
      const v = await streamForm.validateFields();
      const cameraKey = String(v.cameraKey || '').trim().toLowerCase();
      setStreamSaving(true);
      const res = await fetch(ENDPOINTS.DEVICE_VIDEO_STREAM(streamDevice.id, cameraKey), {
        method: 'PUT', headers: buildAuthHeaders(), credentials: 'include',
        body: JSON.stringify({ label: String(v.label || '').trim(), enabled: v.enabled !== false }),
      });
      if (res.ok) {
        message.success('Video stream saved');
        streamForm.setFieldsValue({ cameraKey: 'front', label: 'Front camera', enabled: true });
        await fetchDeviceStreams(streamDevice.id);
      } else { const err = await res.json(); if (await onAuthMismatch(res, err)) return; message.error(err?.message || 'Failed to save video stream'); }
    } catch (e: any) { if (e?.errorFields) return; message.error('Failed to save video stream'); }
    finally { setStreamSaving(false); }
  }, [buildAuthHeaders, fetchDeviceStreams, onAuthMismatch, streamDevice, streamForm]);

  const deleteDeviceStream = useCallback(async (stream: DeviceVideoStreamItem) => {
    if (!streamDevice) return;
    const res = await fetch(ENDPOINTS.DEVICE_VIDEO_STREAM(streamDevice.id, stream.cameraKey), {
      method: 'DELETE', headers: buildAuthHeaders(), credentials: 'include',
    });
    if (res.ok) {
      message.success('Video stream removed');
      await fetchDeviceStreams(streamDevice.id);
    } else { const err = await res.json(); if (await onAuthMismatch(res, err)) return; message.error(err?.message || 'Failed to remove video stream'); }
  }, [buildAuthHeaders, fetchDeviceStreams, onAuthMismatch, streamDevice]);

  const saveDeviceType = useCallback(async () => {
    try {
      const v = await typeForm.validateFields();
      const templates = Array.isArray(v.moduleTemplates)
        ? v.moduleTemplates.map((item: any, i: number) => ({
            moduleDefinitionId: item?.moduleDefinitionId ?? null,
            name: normalizeOptionalField(item?.name),
            instanceKey: normalizeOptionalField(item?.instanceKey),
            isDefault: item?.isDefault ?? true,
            config: item?.config && typeof item.config === 'object' && !Array.isArray(item.config)
              ? item.config
              : parseJsonObjectText(item?.configText, `Module template ${i + 1} config`, true),
            metadata: parseJsonObjectText(item?.metadataText, `Module template ${i + 1} metadata`, true),
          }))
        : [];
      const dupes = new Set<string>();
      templates.forEach((c: any) => { const k = String(c.instanceKey || c.moduleDefinitionId).toLowerCase(); if (dupes.has(k)) throw new Error(`Module template "${k}" is duplicated`); dupes.add(k); });
      const capabilities = buildCapabilityPayload(Array.isArray(v.capabilityIds) ? v.capabilityIds : []);

      const hasMission = v.defaultAltitudeMode || v.defaultCruiseSpeed || v.defaultHoverSpeed || (Array.isArray(v.availableCommands) && v.availableCommands.length > 0);
      const vehicleCapabilities: VehicleCapabilities | null = hasMission ? {
        defaultAltitudeMode: normalizeOptionalField(v.defaultAltitudeMode),
        defaultCruiseSpeed: typeof v.defaultCruiseSpeed === 'number' ? v.defaultCruiseSpeed : null,
        defaultHoverSpeed: typeof v.defaultHoverSpeed === 'number' ? v.defaultHoverSpeed : null,
        availableCommands: Array.isArray(v.availableCommands) && v.availableCommands.length > 0 ? v.availableCommands : null,
      } : null;

      setTypeSaving(true);
      const res = await fetch(`${API_BASE_URL}/device-types`, {
        method: 'POST', headers: buildAuthHeaders(), credentials: 'include',
        body: JSON.stringify({ name: String(v.name || '').trim(), description: normalizeOptionalField(v.description), moduleTemplates: templates, capabilities, vehicleCapabilities, vehicleProfileId: typeof v.vehicleProfileId === 'number' ? v.vehicleProfileId : null }),
      });
      if (res.ok) { message.success('Device type created'); closeTypeModal(); await fetchDeviceTypes(); }
      else { const err = await res.json(); if (await onAuthMismatch(res, err)) return; message.error(err?.message || 'Failed to save'); }
    } catch (e: any) { if (e?.errorFields) return; message.error(e instanceof Error ? e.message : 'Failed to save device type'); }
    finally { setTypeSaving(false); }
  }, [buildAuthHeaders, buildCapabilityPayload, closeTypeModal, fetchDeviceTypes, onAuthMismatch, typeForm]);

  const saveConfig = useCallback(async () => {
    if (!configDevice) return;
    try {
      const v = await configForm.validateFields();
      setConfigSaving(true);
      const res = await fetch(`${API_BASE_URL}/devices/${configDevice.id}/configurations`, {
        method: 'POST', headers: buildAuthHeaders(), credentials: 'include',
        body: JSON.stringify({ configJson: parseJsonObjectText(v.configJsonText, 'Configuration JSON'), appliedAt: v.appliedAt ? v.appliedAt.toISOString() : null }),
      });
      if (res.ok) { message.success('Configuration added'); configForm.setFieldsValue({ configJsonText: '{\n  \n}', appliedAt: null }); await fetchDeviceConfigurations(configDevice.id); }
      else { const err = await res.json(); if (await onAuthMismatch(res, err)) return; message.error(err?.message || 'Failed to save'); }
    } catch (e: any) { if (e?.errorFields) return; message.error(e instanceof Error ? e.message : 'Failed to save'); }
    finally { setConfigSaving(false); }
  }, [buildAuthHeaders, configDevice, configForm, fetchDeviceConfigurations, onAuthMismatch]);

  const syncDeviceModulesFromType = useCallback(async (device: DeviceItem, mode: 'missing_only' | 'replace_all') => {
    setSyncingDeviceId(device.id);
    try {
      const res = await fetch(`${API_BASE_URL}/devices/${device.id}/sync-modules-from-type`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ mode }),
      });

      if (res.ok) {
        const payload = await res.json();
        const summary = payload?.summary || {};
        message.success(
          mode === 'replace_all'
            ? `Replaced modules from type (${summary.createdModules ?? 0} created)`
            : `Synced ${summary.createdModules ?? 0} missing module${summary.createdModules === 1 ? '' : 's'}`
        );
        await Promise.all([fetchDevices(), fetchDeviceTypes()]);
      } else {
        const err = await res.json();
        if (await onAuthMismatch(res, err)) return;
        message.error(err?.message || err?.error || 'Failed to sync modules');
      }
    } catch (error) {
      console.error(error);
      message.error('Failed to sync modules');
    } finally {
      setSyncingDeviceId(null);
    }
  }, [buildAuthHeaders, fetchDeviceTypes, fetchDevices, onAuthMismatch]);

  const confirmReplaceDeviceModules = useCallback((device: DeviceItem) => {
    Modal.confirm({
      title: `Replace modules for ${device.label}?`,
      content: 'This deletes the physical module list for this device and recreates it from the current device type templates. Device-specific module configuration will be overwritten.',
      okText: 'Replace from type',
      okButtonProps: { danger: true },
      onOk: () => syncDeviceModulesFromType(device, 'replace_all'),
    });
  }, [syncDeviceModulesFromType]);

  /* ── Column definitions ── */
  const typeColumns = useMemo<ColumnsType<DeviceTypeItem>>(() => [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70, sorter: (a, b) => a.id - b.id, render: (v: number) => <Text code>{v}</Text> },
    { title: 'Type', key: 'type', sorter: (a, b) => a.name.localeCompare(b.name), render: (_, r) => (<Space direction="vertical" size={0}><Text strong>{r.name}</Text><Text type="secondary">{r.description || 'No description'}</Text></Space>) },
    { title: 'Capabilities', key: 'capabilities', render: (_, r) => renderCapabilityTags(r.capabilities) },
    { title: 'Templates', key: 'templates', render: (_, r) => <Space wrap size={[4, 4]}>{(r.moduleTemplates || []).length > 0 ? r.moduleTemplates.map((c) => <Tag key={c.id || c.instanceKey || c.moduleDefinitionId} color="geekblue">{c.moduleDefinition?.key || c.name || c.moduleDefinitionId}</Tag>) : <Text type="secondary">None</Text>}</Space> },
    { title: 'Devices', dataIndex: 'deviceCount', key: 'count', width: 90, sorter: (a, b) => (a.deviceCount || 0) - (b.deviceCount || 0) },
    { title: 'Actions', key: 'actions', width: 140, render: (_, r) => (
      <Space size="small">
        <Button type="link" icon={<EyeOutlined />} onClick={() => setSelectedTypeId(r.id)}>View</Button>
      </Space>
    ) },
  ], [renderCapabilityTags]);

  const deviceColumns = useMemo<ColumnsType<DeviceItem>>(() => [
    {
      title: 'Device', key: 'device', width: 230,
      sorter: (a, b) => a.label.localeCompare(b.label),
      render: (_, r) => (
        <Space direction="vertical" size={1} className="back-office-device-identity">
          <Text strong ellipsis={{ tooltip: r.label }}>{r.label}</Text>
          <Text type="secondary" ellipsis={{ tooltip: [r.serialNumber, r.codePrefix].filter(Boolean).join(' · ') }}>
            #{r.id} · {r.serialNumber || 'No serial'}{r.codePrefix ? ` · ${r.codePrefix}` : ''}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Type', key: 'type', width: 150, ellipsis: true,
      render: (_, r) => { const t = resolveDeviceType(r); return t ? <Text ellipsis={{ tooltip: t.name }}>{t.name}</Text> : <Text type="secondary">No type</Text>; },
      sorter: (a, b) => (resolveDeviceType(a)?.name || '').localeCompare(resolveDeviceType(b)?.name || ''),
    },
    {
      title: 'Modules', key: 'modules', width: 190,
      render: (_, r) => {
        const modules = r.modules || [];
        if (modules.length === 0) return <Text type="secondary">None</Text>;
        const label = (module: typeof modules[number]) => String(module.moduleDefinition?.key || module.name || module.moduleDefinitionId);
        return (
          <Space wrap size={[4, 4]} className="back-office-device-tags">
            {modules.slice(0, 3).map((module) => <Tag key={module.id || module.instanceKey || module.moduleDefinitionId} title={label(module)}>{label(module)}</Tag>)}
            {modules.length > 3 && <Tooltip title={modules.slice(3).map(label).join(', ')}><Tag>+{modules.length - 3}</Tag></Tooltip>}
          </Space>
        );
      },
    },
    { title: 'Capabilities', key: 'capabilities', width: 220, render: (_, r) => renderCapabilityTags(r.effectiveCapabilities || resolveDeviceType(r)?.capabilities) },
    {
      title: 'Company', key: 'company', width: 170, ellipsis: true,
      render: (_, r) => r.company?.id ? <Tag color="blue" title={formatCompanyLabel(r.company)}>{formatCompanyLabel(r.company)}</Tag> : <Tag>Unassigned</Tag>,
      sorter: (a, b) => (a.company?.name || '').localeCompare(b.company?.name || ''),
    },
    {
      title: 'Status', dataIndex: 'currentStatus', key: 'status', width: 120,
      render: (value: unknown) => { const status = getDeviceStatusLabel(value); return <Tag color={statusColorMap[status] || 'default'}>{status}</Tag>; },
    },
    { title: 'Created', dataIndex: 'createdAt', key: 'created', width: 150, render: (v?: string) => formatTimestamp(v) },
    { title: 'Actions', key: 'actions', width: 124, fixed: 'right', render: (_, r) => {
      const type = resolveDeviceType(r);
      const canSync = Boolean(type?.moduleTemplates?.length);
      const syncing = syncingDeviceId === r.id;
      return (
        <Space size={4} className="back-office-device-actions">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditDevice(r)}>Edit</Button>
          <Dropdown trigger={['click']} placement="bottomRight" menu={{ items: [
            { key: 'tokens', icon: <KeyOutlined />, label: 'Device credentials', onClick: () => openTokenModal(r) },
            { key: 'video', icon: <EyeOutlined />, label: 'Video streams', disabled: !r.companyId, onClick: () => openStreamModal(r) },
            { key: 'config', icon: <SettingOutlined />, label: 'Configurations', onClick: () => openConfigModal(r) },
            { type: 'divider' },
            { key: 'sync', icon: <SyncOutlined spin={syncing} />, label: 'Sync missing modules', disabled: !canSync || syncing, onClick: () => syncDeviceModulesFromType(r, 'missing_only') },
            { key: 'replace', danger: true, label: 'Replace modules from type', disabled: !canSync || syncing, onClick: () => confirmReplaceDeviceModules(r) },
          ] }}>
            <Button size="small" aria-label={`More actions for ${r.label}`} icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      );
    } },
  ], [confirmReplaceDeviceModules, formatCompanyLabel, openConfigModal, openEditDevice, openStreamModal, openTokenModal, renderCapabilityTags, resolveDeviceType, syncDeviceModulesFromType, syncingDeviceId]);

  const configColumns = useMemo<ColumnsType<DeviceConfigurationItem>>(() => [
    { title: 'Version', dataIndex: 'configVersion', key: 'v', width: 100, render: (v: number) => <Text strong>v{v}</Text> },
    { title: 'Created', dataIndex: 'createdAt', key: 'created', render: (v?: string) => formatTimestamp(v) },
    { title: 'Applied', dataIndex: 'appliedAt', key: 'applied', render: (v?: string | null) => v ? formatTimestamp(v) : <Text type="secondary">Pending</Text> },
  ], []);

  const tokenColumns = useMemo<ColumnsType<DeviceAuthTokenItem>>(() => [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (v: string, r) => <Space direction="vertical" size={0}><Text strong>{v}</Text><Text code>{r.maskedToken || `${r.tokenPrefix}...`}</Text></Space> },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100, render: (v: string) => <Tag color={v === 'active' ? 'green' : v === 'revoked' ? 'red' : 'default'}>{v}</Tag> },
    { title: 'Created', dataIndex: 'createdAt', key: 'createdAt', width: 150, render: (v?: string) => formatTimestamp(v) },
    { title: 'Last used', dataIndex: 'lastUsedAt', key: 'lastUsedAt', width: 150, render: (v?: string | null) => formatTimestamp(v) },
    { title: 'Actions', key: 'actions', width: 170, render: (_, r) => (
      <Space size="small">
        <Button size="small" icon={<SyncOutlined />} disabled={r.status !== 'active'} onClick={() => rotateDeviceToken(r)}>Rotate</Button>
        <Button size="small" danger icon={<StopOutlined />} disabled={r.status !== 'active'} onClick={() => revokeDeviceToken(r)}>Revoke</Button>
      </Space>
    ) },
  ], [revokeDeviceToken, rotateDeviceToken]);

  const streamColumns = useMemo<ColumnsType<DeviceVideoStreamItem>>(() => [
    { title: 'Camera', key: 'camera', render: (_, r) => <Space direction="vertical" size={0}><Text strong>{r.label}</Text><Text code>{r.cameraKey}</Text></Space> },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100, render: (v: string) => <Tag color={v === 'live' ? 'green' : v === 'stale' ? 'orange' : v === 'waiting' ? 'blue' : 'default'}>{v}</Tag> },
    { title: 'Last frame', dataIndex: 'lastFrameAt', key: 'lastFrameAt', width: 160, render: (v?: string | null) => formatTimestamp(v) },
    { title: 'Enabled', dataIndex: 'enabled', key: 'enabled', width: 90, render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? 'yes' : 'no'}</Tag> },
    { title: 'Actions', key: 'actions', width: 90, render: (_, r) => <Button size="small" danger onClick={() => deleteDeviceStream(r)}>Delete</Button> },
  ], [deleteDeviceStream]);

  /* Derive show/hide from the selected VehicleProfile's vehicleClass (not firmwareType) */
  const watchedProfileId = Form.useWatch('vehicleProfileId', typeForm);
  const selectedProfile = vehicleProfiles.find((vp) => vp.id === watchedProfileId);
  const vehicleClass = selectedProfile?.vehicleClass ?? null;
  const showAltitude = !vehicleClass || vehicleClass !== 'ground';
  const showHoverSpeed = !vehicleClass || ['multirotor', 'vtol', 'submarine'].includes(vehicleClass);

  const configDeviceType = useMemo(() => configDevice ? resolveDeviceType(configDevice) : null, [configDevice, resolveDeviceType]);

  /* ── If a device type is selected, show its detail view ── */
  if (selectedTypeId !== null) {
    return (
      <DeviceTypeDetail
        deviceTypeId={selectedTypeId}
        onBack={() => setSelectedTypeId(null)}
        buildAuthHeaders={buildAuthHeaders}
        onAuthMismatch={onAuthMismatch}
        onSaved={() => fetchDeviceTypes()}
      />
    );
  }

  /* ── Main list view ── */
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert type="info" showIcon message="Global device registry and catalog" description="Manage device types, modules, and the company-wide device inventory. Click View on a device type to see and edit its modules." />

      {/* Device Types table */}
      <Card className="back-office-registry-card" title="Device Types" extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreateType}>Add device type</Button>}>
        <div className="back-office-registry-toolbar">
          <Input.Search allowClear placeholder="Search types…" className="back-office-registry-search" value={typeSearchTerm} onChange={(e) => setTypeSearchTerm(e.target.value)} />
          <Space size="middle">
            <Text type="secondary">{filteredDeviceTypes.length} type{filteredDeviceTypes.length === 1 ? '' : 's'}</Text>
            <Button icon={<ReloadOutlined />} onClick={() => fetchDeviceTypes()}>Refresh</Button>
          </Space>
        </div>
        <Table className="back-office-registry-table" rowKey="id" loading={typeLoading} columns={typeColumns} dataSource={filteredDeviceTypes} pagination={{ pageSize: 8, showSizeChanger: false }} scroll={{ x: 960 }} tableLayout="fixed" />
      </Card>

      {/* Devices table */}
      <Card className="back-office-registry-card" title="Devices" extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreateDevice}>Add device</Button>}>
        <div className="back-office-registry-toolbar">
          <div className="back-office-registry-filters">
            <Select value={companyFilter} showSearch className="back-office-company-filter" optionFilterProp="label"
              options={[{ value: 'all', label: 'All companies' }, { value: 'unassigned', label: 'Unassigned only' }, ...companies.map((c) => ({ value: c.id, label: formatCompanyLabel(c) }))]}
              onChange={(v) => setCompanyFilter(v as CompanyFilterValue)} />
            <Input.Search allowClear placeholder="Search devices…" className="back-office-registry-search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <Space size="middle">
            <Text type="secondary">{filteredDevices.length} device{filteredDevices.length === 1 ? '' : 's'}</Text>
            <Button icon={<ReloadOutlined />} onClick={() => Promise.all([fetchDevices(), fetchDeviceTypes()])}>Refresh</Button>
          </Space>
        </div>
        <Table
          className="back-office-registry-table back-office-devices-table"
          rowKey="id"
          loading={loading}
          columns={deviceColumns}
          dataSource={filteredDevices}
          pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
          scroll={{ x: 1354 }}
          tableLayout="fixed"
        />
      </Card>

      {/* Create device type modal */}
      <Modal title="Create device type" open={typeModalOpen} onCancel={closeTypeModal} onOk={saveDeviceType} confirmLoading={typeSaving} okText="Save" width={860} destroyOnClose>
        <Form form={typeForm} layout="vertical">
          <Form.Item label="Name" name="name" rules={[{ required: true }, { max: 100 }]}><Input placeholder="Lightbox Standard" /></Form.Item>
          <Form.Item label="Description" name="description" rules={[{ max: 1000 }]}><Input.TextArea rows={2} /></Form.Item>
          <Divider orientation="left"><Text strong>Mission Profile</Text></Divider>
          <Form.Item label="Vehicle profile" name="vehicleProfileId" help="Links this device type to a reusable vehicle platform profile.">
            <Select allowClear placeholder="None — stationary device" optionFilterProp="label"
              options={vehicleProfiles.map((vp) => ({ value: vp.id, label: `${vp.name} (${vp.vehicleClass})` }))} />
          </Form.Item>
          {showAltitude && <Form.Item label="Default altitude mode" name="defaultAltitudeMode">
            <Select allowClear placeholder="Not set" options={[{ value: 'relative', label: 'Relative to Home' }, { value: 'absolute', label: 'Absolute (AMSL)' }, { value: 'terrain', label: 'Terrain Following' }]} />
          </Form.Item>}
          <Space size="middle" style={{ width: '100%' }}>
            <Form.Item label="Cruise speed (m/s)" name="defaultCruiseSpeed" style={{ flex: 1 }}><InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} /></Form.Item>
            {showHoverSpeed && <Form.Item label="Hover speed (m/s)" name="defaultHoverSpeed" style={{ flex: 1 }}><InputNumber min={0} max={50} step={0.5} style={{ width: '100%' }} /></Form.Item>}
          </Space>
          <Form.Item label="Supported commands" name="availableCommands">
            <Select mode="multiple" allowClear placeholder="All (no filter)" optionFilterProp="label" options={COMMAND_CATALOGUE.map((cmd) => ({ value: cmd.id, label: `${cmd.displayName} (${cmd.kind})` }))} />
          </Form.Item>
          <Divider orientation="left"><Text strong>Capabilities</Text></Divider>
          <Form.Item label="Platform capabilities" name="capabilityIds" extra="Asset abilities used by mission eligibility. Modules remain triggerable components.">
            <Select mode="multiple" allowClear showSearch placeholder="Select capabilities" optionFilterProp="label" options={capabilitySelectOptions} />
          </Form.Item>
          <Collapse defaultActiveKey={[]} style={{ marginTop: 8 }} items={[{ key: 'templates', label: <Text strong>Module templates</Text>, children: <ModuleFormList form={typeForm} moduleDefinitions={moduleDefinitions} name="moduleTemplates" title="template" /> }]} />
        </Form>
      </Modal>

      {/* Create/edit device modal */}
      <Modal title={editing ? `Edit device: ${editing.label}` : 'Create device'} open={modalOpen} onCancel={closeDeviceModal} onOk={saveDevice} confirmLoading={saving} okText="Save" destroyOnClose width={1200}>
        <Form form={form} layout="vertical">
          <Form.Item label="Label" name="label" rules={[{ required: true }, { max: 100 }]}><Input placeholder="Lightbox #12" /></Form.Item>
          <Form.Item label="Serial number" name="serialNumber" rules={[{ max: 50 }]}><Input placeholder="LBX-00012" /></Form.Item>
          <Form.Item label="Code prefix" name="codePrefix" rules={[{ max: 50 }]}><Input placeholder="LB12" /></Form.Item>
          <Form.Item label="Device type" name="deviceTypeId" extra="Assign a shared type to inherit modules.">
            <Select allowClear showSearch placeholder="No type" optionFilterProp="label" options={deviceTypes.map((t) => ({ value: t.id, label: `${t.name} (#${t.id})` }))} />
          </Form.Item>
          <Form.Item label="Assigned company" name="companyId" extra="Leave blank for unassigned.">
            <Select allowClear showSearch placeholder="Unassigned" optionFilterProp="label" options={companies.map((c) => ({ value: c.id, label: formatCompanyLabel(c) }))} />
          </Form.Item>
          <Collapse defaultActiveKey={[]} style={{ marginTop: 8 }} items={[{
            key: 'modules',
            label: <Text strong>Physical modules</Text>,
            children: <ModuleFormList form={form} moduleDefinitions={moduleDefinitions} name="modules" title="module" emptyDescription="Leave blank when creating a device to copy defaults from the selected type." visualOnly showMetadata={false} />,
          }]} />
        </Form>
      </Modal>

      {/* Device credential modal */}
      <Modal title={tokenDevice ? `Device credentials: ${tokenDevice.label}` : 'Device credentials'} open={tokenModalOpen} onCancel={closeTokenModal}
        footer={<Space><Button onClick={closeTokenModal}>Close</Button><Button type="primary" icon={<PlusOutlined />} onClick={createDeviceToken} loading={tokenSaving}>Create credential</Button></Space>} width={900} destroyOnClose>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert type="info" showIcon message="Physical device identity" description="This credential identifies the machine independently of its current company. Send it in the x-device-token header. Raw credentials are shown only when created or rotated." />
          <Form form={tokenForm} layout="inline">
            <Form.Item name="name" label="Credential name" rules={[{ required: true, message: 'Required' }]}>
              <Input placeholder="Lightbox machine credential" style={{ minWidth: 280 }} />
            </Form.Item>
          </Form>
          <Table rowKey="id" loading={tokenLoading} columns={tokenColumns} dataSource={deviceTokens} pagination={{ pageSize: 6, showSizeChanger: false }} />
        </Space>
      </Modal>

      {/* Device video streams modal */}
      <Modal title={streamDevice ? `Video streams: ${streamDevice.label}` : 'Video streams'} open={streamModalOpen} onCancel={closeStreamModal}
        footer={<Space><Button onClick={closeStreamModal}>Close</Button><Button type="primary" icon={<PlusOutlined />} onClick={saveDeviceStream} loading={streamSaving}>Save stream</Button></Space>} width={900} destroyOnClose>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert type="info" showIcon message="Backend-relayed MJPEG" description="Robots push JPEG frames to the backend with their x-device-token credential. Operators view the backend MJPEG stream from Device Fleet." />
          <Form form={streamForm} layout="inline">
            <Form.Item name="cameraKey" label="Camera key" rules={[{ required: true, message: 'Required' }, { pattern: /^[a-zA-Z0-9_-]+$/, message: 'Use letters, numbers, underscore, or dash' }]}>
              <Input placeholder="front" style={{ minWidth: 160 }} />
            </Form.Item>
            <Form.Item name="label" label="Label" rules={[{ required: true, message: 'Required' }]}>
              <Input placeholder="Front camera" style={{ minWidth: 220 }} />
            </Form.Item>
            <Form.Item name="enabled" label="Enabled">
              <Select style={{ width: 110 }} options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} />
            </Form.Item>
          </Form>
          <Table rowKey="id" loading={streamLoading} columns={streamColumns} dataSource={deviceStreams} pagination={{ pageSize: 6, showSizeChanger: false }} />
        </Space>
      </Modal>

      {/* Device config modal */}
      <Modal title={configDevice ? `Configurations: ${configDevice.label}` : 'Device configurations'} open={configModalOpen} onCancel={closeConfigModal}
        footer={<Space><Button onClick={closeConfigModal}>Close</Button><Button type="primary" onClick={saveConfig} loading={configSaving}>Add configuration</Button></Space>} width={980} destroyOnClose>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert type={configDeviceType ? 'info' : 'warning'} showIcon
            message={configDeviceType ? `${configDevice?.label} uses ${configDeviceType.name}` : `${configDevice?.label} has no device type assigned`}
            description={configDeviceType ? (
              <Space direction="vertical" size={4}>
                <Text>Capabilities: {((configDevice?.effectiveCapabilities || configDeviceType.capabilities || []).filter((cap) => cap.enabled !== false).map((cap) => cap.name || cap.definition?.name || cap.capabilityDefinition?.name).filter(Boolean).join(', ')) || 'none'}</Text>
                <Text>Modules: {(configDevice?.modules || []).length > 0 ? (configDevice?.modules || []).map((c) => c.moduleDefinition?.key || c.name || c.moduleDefinitionId).join(', ') : 'none'}</Text>
              </Space>
            ) : 'Assign a device type first for a clearer catalog.'} />
          <Card size="small" title="Add configuration snapshot">
            <Form form={configForm} layout="vertical">
              <Form.Item label="Configuration JSON" name="configJsonText" rules={[{ required: true }]}><Input.TextArea rows={8} placeholder={'{\n  "samplingRateSeconds": 30\n}'} /></Form.Item>
              <Form.Item label="Applied at" name="appliedAt" extra="Optional timestamp."><DatePicker showTime style={{ width: '100%' }} /></Form.Item>
            </Form>
          </Card>
          <Card size="small" title="Configuration history">
            <Table rowKey="id" loading={configLoading} columns={configColumns} dataSource={configurations} pagination={{ pageSize: 6 }}
              expandable={{ expandedRowRender: (r) => <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{stringifyJson(r.configJson) || '{}'}</pre> }} />
          </Card>
        </Space>
      </Modal>
    </Space>
  );
};

export default BackOfficeDevicesTab;
